// src/components/IncomingOrders/AddressCompareTable.jsx

import clsx from "clsx";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import EditButtonIcon from "@mui/icons-material/EditOutlined";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { makeStyles } from "@mui/styles";
import { createAddressFieldPath, hasStringValue, normalizeAddress, ADDRESS_FIELDS as FIELD_ORDER } from "./helpers/addressUtils";
import buildAddressCompareRows from "./helpers/buildAddressCompareRows";

const useStyles = makeStyles((theme) => ({
  table: {
    "& tbody tr:nth-of-type(odd)": {
      backgroundColor: "rgba(0,0,0,.03)",
    },
    borderRadius: 6,
    overflow: "hidden",
    "@media print": {
      tableLayout: "fixed",
      width: "100%",
      display: "table",
    },
  },
  th: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    borderBottom: "none",
    color: theme.palette.text.secondary,
  },
  labelCell: {
    width: 140,
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.primary,
    whiteSpace: "normal",
    "& .titlePart": {
      whiteSpace: "nowrap",
    },
  },
  td: {
    fontSize: 12,
    "@media print": {
      wordWrap: "break-word",
    },
  },
  empty: {
    opacity: 0.35,
    fontStyle: "italic",
  },
  printOnly: {
    display: "none",
    "@media print": { display: "table" },
  },
  // --- editable UX ---
  editableCell: {
    position: "relative",
    paddingRight: 34, // plats för penna
    cursor: "text",
    "&:hover $editBtn": {
      opacity: 1,
      transform: "translateY(-50%) scale(1)",
    },
  },
  editBtn: {
    position: "absolute",
    right: 6,
    top: "50%",
    transform: "translateY(-50%) scale(0.95)",
    opacity: 0,
    transition: "opacity 120ms ease, transform 120ms ease",
    padding: 4,
  },
  inputRoot: {
    fontSize: 12,
  },
  inputBase: {
    fontSize: 12,
    padding: "2px 0",
  },
  dirtyCell: {
    backgroundColor: "rgba(255, 193, 7, 0.12)", // mjuk gul ton
    boxShadow: "inset 3px 0 0 #f7bd02", // accent på vänsterkant (din färg!)
    transition: "background-color 150ms ease",
  },
  editingCell: {
    paddingTop: 5,
    paddingBottom: 0,
  },
}));

const AddressCompareTable = ({
  columns,
  title,
  className,
  printOnly,
  editable,
  isFieldDirty,
  onChangeCell,
}) => {
  const classes = useStyles();

  const normalizedColumns = useMemo(
    () =>
      columns.map((col) => ({
        ...col,
        address: normalizeAddress(col.address),
      })),
    [columns]
  );

  const tableRows = useMemo(() => (
    buildAddressCompareRows(normalizedColumns, editable, FIELD_ORDER)
  ), [normalizedColumns, editable]);

  const [editing, setEditing] = useState(null); 

  useEffect(() => {
    setEditing(null);
  }, [columns]);

  const isCellEditable = (editType) =>
    editable && !printOnly && typeof onChangeCell === "function" && !!editType;

  const isEditing = (editType, key) =>
    editing?.editType === editType && editing?.key === key;

  const startEdit = (editType, key, currentValue) => {
    setEditing(prev => {
      if (prev || !editable || printOnly) return prev ?? null;

      return {
        editType,
        key,
        original: currentValue ?? "",
        draft: currentValue ?? "",
      };
    });
  };

  const cancelEdit = () => {
    setEditing(null);
  };

  const commitEdit = () => {
    setEditing(prev => {
      if (!prev) return null;

      const trimmedDraft = String(prev.draft ?? "").trim();
      const original = String(prev.original ?? "").trim();

      if (onChangeCell && trimmedDraft !== original) {
        onChangeCell(prev.editType, prev.key, trimmedDraft);
      }

      return null;
    });
  };

  if (!tableRows.length) return null;

  return (
    <Table
      className={clsx(
        classes.table,
        printOnly && classes.printOnly,
        className
      )}
      size="small"
    >
      <TableHead>
        <TableRow>
          <TableCell className={classes.th}>
            <Typography className={classes.th}>{title}</Typography>
          </TableCell>
          {normalizedColumns.map((col, colIdx) => (
            <TableCell key={`${col.editType}-${colIdx}`} className={classes.th}>
              {col.title}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {tableRows.map((row) => (
          <TableRow key={row.rowId}>
            <TableCell className={`${classes.td} ${classes.labelCell}`} sx={{ textTransform: "capitalize" }}>
              <Box className={classes.titlePart} >{row.title}</Box>
            </TableCell>

            {normalizedColumns.map((col, colIdx) => {
              const { editType, address } = col;

              const key = row.rowId + '-' + colIdx;
              const isContentCell = row.columnIndexSet.has(colIdx);

              if (!isContentCell) {
                return (
                  <TableCell key={key}></TableCell>
                );
              }

              const val = address?.[row.key];
              const editableHere = isCellEditable(editType);
              const addressFieldPath = createAddressFieldPath(editType, row.key);
              const isDirtyCell = addressFieldPath && isFieldDirty?.(addressFieldPath);
              const isEditingCell = isEditing(editType, row.key);

              return (
                <TableCell
                  key={key}
                  className={clsx(
                    classes.td,
                    editableHere && classes.editableCell,
                    isDirtyCell && classes.dirtyCell,
                    isEditingCell && classes.editingCell
                  )}
                  onClick={() => editableHere && startEdit(editType, row.key, val)}
                >
                  {editableHere && (
                    <Tooltip title="Redigera">
                      <IconButton
                        className={classes.editBtn}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          startEdit(editType, row.key, val);
                        }}
                      >
                        <EditButtonIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  )}

                  {editableHere && isEditingCell ? (
                    <TextField
                      autoFocus
                      fullWidth
                      variant="standard"
                      value={editing.draft}
                      onChange={(e) =>
                        setEditing((prev) =>
                          prev ? { ...prev, draft: e.target.value } : prev
                        )
                      }
                      onBlur={commitEdit}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          commitEdit();
                        }
                        if (e.key === "Escape") {
                          e.preventDefault();
                          cancelEdit();
                        }
                      }}
                      InputProps={{
                        classes: { root: classes.inputRoot, input: classes.inputBase },
                      }}
                    />
                  ) : (
                    hasStringValue(val) ? val : <span className={classes.empty}>—</span>
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

AddressCompareTable.propTypes = {
  title: PropTypes.string.isRequired,
  className: PropTypes.string,
  printOnly: PropTypes.bool,
  editable: PropTypes.bool,
  isFieldDirty: PropTypes.func,
  onChangeCell: PropTypes.func,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      editType: PropTypes.oneOf(["delivery", "invoice", "orderer"]).isRequired,
      title: PropTypes.string.isRequired,
      address: PropTypes.object,
      getAddressLabel: PropTypes.func.isRequired,
    })
  ).isRequired,
};

AddressCompareTable.defaultProps = {
  className: "",
  printOnly: false,
  editable: false,
  isFieldDirty: null,
  onChangeCell: null,
};

AddressCompareTable.displayName = "AddressCompareTable";

export default AddressCompareTable;
